import React, { useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
    CheckCircle2,
    Home,
    Calendar,
    CreditCard,
    FileText,
    ShieldCheck,
    Download,
    Mail,
    Phone,
    Clock,
    MapPin,
    User
} from "lucide-react";
import confetti from "canvas-confetti";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
const PaymentSuccess = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const contentRef = useRef<HTMLDivElement>(null);

    const stateData = useMemo(() => {
        if (location.state && location.state.bookingId) {
            try {
                sessionStorage.setItem("last_payment_success_state", JSON.stringify(location.state));
            } catch (e) {}
            return location.state;
        }
        try {
            const cached = sessionStorage.getItem("last_payment_success_state");
            if (cached) return JSON.parse(cached);
        } catch (e) {}
        return null;
    }, [location.state]);

    const { 
        bookingId = "MTF-" + Math.random().toString(36).substr(2, 9).toUpperCase(),
        amount = "0",
        serviceName = "Wellness Service",
        date = new Date().toLocaleDateString('en-GB'),
        duration = "Flexible",
        location: pkgLocation = "Flexible",
        customerName,
        customerEmail,
        customerPhone,
        name,
        email,
        phone,
        userName,
        userEmail,
        userPhone,
    } = stateData || location.state || {};

    const finalName = customerName || userName || name || "";
    const finalEmail = customerEmail || userEmail || email || "";
    const finalPhone = customerPhone || userPhone || phone || "";

    useEffect(() => {
        // Trigger confetti on mount
        const duration = 3 * 1000;
        const animationEnd = Date.now() + duration;
        const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

        const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

        const interval: any = setInterval(function () {
            const timeLeft = animationEnd - Date.now();

            if (timeLeft <= 0) {
                return clearInterval(interval);
            }

            const particleCount = 50 * (timeLeft / duration);
            confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
            confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
        }, 250);

        return () => clearInterval(interval);
    }, []);

    const handleDownloadPDF = async () => {
        if (contentRef.current) {
            // Find all elements with class 'no-print' and hide them
            const noPrintElements = contentRef.current.querySelectorAll('.no-print');
            noPrintElements.forEach((el) => {
                (el as HTMLElement).style.display = 'none';
            });

            try {
                const canvas = await html2canvas(contentRef.current, {
                    scale: 2,
                    useCORS: true,
                    backgroundColor: "#ffffff",
                    logging: false
                });
                const imgData = canvas.toDataURL("image/png");
                const pdf = new jsPDF("p", "mm", "a4");
                const imgProps = pdf.getImageProperties(imgData);
                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = pdf.internal.pageSize.getHeight();

                // Set professional margins (15mm)
                const margin = 15;
                const contentWidth = pdfWidth - (margin * 2);
                const contentHeight = (imgProps.height * contentWidth) / imgProps.width;

                // Center vertically if it fits, otherwise align to top margin
                const xOffset = margin;
                const yOffset = contentHeight < (pdfHeight - (margin * 2))
                    ? (pdfHeight - contentHeight) / 2
                    : margin;

                pdf.addImage(imgData, "PNG", xOffset, yOffset, contentWidth, contentHeight);
                pdf.save(`booking-confirmation-${bookingId}.pdf`);
            } catch (error) {
                console.error("Error generating PDF:", error);
            } finally {
                // Restore hidden elements
                noPrintElements.forEach((el) => {
                    (el as HTMLElement).style.display = '';
                });
            }
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#F5F0E3] via-[#F8F4EA] to-[#EFE8D6] pt-16 pb-14 px-4 relative overflow-hidden">
            <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-emerald-400/10 to-transparent pointer-events-none" />
            <div className="max-w-3xl mx-auto relative z-10">
                {/* Success Card */}
                <motion.div
                    ref={contentRef}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-[rgba(255,253,247,0.94)] backdrop-blur-xl rounded-[2.5rem] shadow-[0_24px_60px_rgba(21,48,43,0.14)] border border-[rgba(21,48,43,0.10)] overflow-hidden"
                >
                    {/* Top Accent Bar */}
                    <div className="h-2 bg-gradient-to-r from-[#3F6652] via-[#E6A93F] to-[#57628F]" />

                    <div className="p-8 md:p-12 text-center">
                        {/* Animated Checkmark */}
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", damping: 12, stiffness: 200, delay: 0.2 }}
                            className="w-24 h-24 bg-[rgba(63,102,82,0.08)] rounded-full flex items-center justify-center mx-auto mb-8 relative"
                        >
                            <motion.div
                                animate={{ scale: [1, 1.2, 1] }}
                                transition={{ repeat: Infinity, duration: 2 }}
                                className="absolute inset-0 bg-[rgba(63,102,82,0.12)] rounded-full opacity-60"
                            />
                            <CheckCircle2 className="w-12 h-12 text-[#3F6652] relative z-10" />
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                        >
                            <h1 className="text-3xl md:text-4xl font-bold text-[#15302B] mb-4">
                                Payment Successful!
                            </h1>
                            <p className="text-[#3B5750] text-lg mb-8 max-w-md mx-auto">
                                Thank you for choosing Metafit. Your booking has been confirmed and we've sent the details to your email and Whatsapp.
                            </p>
                        </motion.div>

                        {/* Booking Details Grid */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.6 }}
                            className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left mb-10"
                        >
                            {finalName && (
                                <div className="p-5 bg-[rgba(245,240,227,0.85)] rounded-2xl border border-[rgba(21,48,43,0.08)]">
                                    <div className="flex items-center gap-3 mb-1">
                                        <User className="w-4 h-4 text-[#3F6652]" />
                                        <span className="text-xs font-semibold text-[#57628F] uppercase tracking-wider">Guest Name</span>
                                    </div>
                                    <p className="text-[#15302B] font-bold">{finalName}</p>
                                </div>
                            )}

                            {finalPhone && (
                                <div className="p-5 bg-[rgba(245,240,227,0.85)] rounded-2xl border border-[rgba(21,48,43,0.08)]">
                                    <div className="flex items-center gap-3 mb-1">
                                        <Phone className="w-4 h-4 text-[#3F6652]" />
                                        <span className="text-xs font-semibold text-[#57628F] uppercase tracking-wider">Phone Number</span>
                                    </div>
                                    <p className="text-[#15302B] font-bold">{finalPhone}</p>
                                </div>
                            )}

                            {finalEmail && (
                                <div className="p-5 bg-[rgba(245,240,227,0.85)] rounded-2xl border border-[rgba(21,48,43,0.08)]">
                                    <div className="flex items-center gap-3 mb-1">
                                        <Mail className="w-4 h-4 text-[#3F6652]" />
                                        <span className="text-xs font-semibold text-[#57628F] uppercase tracking-wider">Email Address</span>
                                    </div>
                                    <p className="text-[#15302B] font-bold truncate">{finalEmail}</p>
                                </div>
                            )}

                            <div className="p-5 bg-[rgba(245,240,227,0.85)] rounded-2xl border border-[rgba(21,48,43,0.08)]">
                                <div className="flex items-center gap-3 mb-1">
                                    <FileText className="w-4 h-4 text-[#3F6652]" />
                                    <span className="text-xs font-semibold text-[#57628F] uppercase tracking-wider">Booking ID</span>
                                </div>
                                <p className="text-[#15302B] font-bold font-mono">{bookingId}</p>
                            </div>

                            <div className="p-5 bg-[rgba(245,240,227,0.85)] rounded-2xl border border-[rgba(21,48,43,0.08)]">
                                <div className="flex items-center gap-3 mb-1">
                                    <Calendar className="w-4 h-4 text-[#3F6652]" />
                                    <span className="text-xs font-semibold text-[#57628F] uppercase tracking-wider">Service Date</span>
                                </div>
                                <p className="text-[#15302B] font-bold">{date}</p>
                            </div>

                            <div className="p-5 bg-[rgba(245,240,227,0.85)] rounded-2xl border border-[rgba(21,48,43,0.08)]">
                                <div className="flex items-center gap-3 mb-1">
                                    <ShieldCheck className="w-4 h-4 text-[#3F6652]" />
                                    <span className="text-xs font-semibold text-[#57628F] uppercase tracking-wider">Service</span>
                                </div>
                                <p className="text-[#15302B] font-bold">{serviceName}</p>
                            </div>

                            <div className="p-5 bg-[rgba(245,240,227,0.85)] rounded-2xl border border-[rgba(21,48,43,0.08)]">
                                <div className="flex items-center gap-3 mb-1">
                                    <Clock className="w-4 h-4 text-[#3F6652]" />
                                    <span className="text-xs font-semibold text-[#57628F] uppercase tracking-wider">Duration / Time</span>
                                </div>
                                <p className="text-[#15302B] font-bold">{duration || "Flexible"}</p>
                            </div>

                            <div className="p-5 bg-[rgba(245,240,227,0.85)] rounded-2xl border border-[rgba(21,48,43,0.08)]">
                                <div className="flex items-center gap-3 mb-1">
                                    <MapPin className="w-4 h-4 text-[#3F6652]" />
                                    <span className="text-xs font-semibold text-[#57628F] uppercase tracking-wider">Location</span>
                                </div>
                                <p className="text-[#15302B] font-bold truncate">{pkgLocation || "Flexible"}</p>
                            </div>

                            <div className="p-5 bg-[rgba(245,240,227,0.85)] rounded-2xl border border-[rgba(21,48,43,0.08)]">
                                <div className="flex items-center gap-3 mb-1">
                                    <CreditCard className="w-4 h-4 text-[#3F6652]" />
                                    <span className="text-xs font-semibold text-[#57628F] uppercase tracking-wider">Amount Paid</span>
                                </div>
                                <p className="text-[#3F6652] font-bold">₹{amount}</p>
                            </div>
                        </motion.div>

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row gap-4 no-print">
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => navigate("/")}
                                className="flex-1 bg-[#15302B] text-[#F5F0E3] px-8 py-4 rounded-2xl font-bold flex items-center justify-center gap-3 hover:bg-[#0f241f] transition-all shadow-xl shadow-[rgba(21,48,43,0.18)]"
                            >
                                <Home className="w-5 h-5" />
                                Return Home
                            </motion.button>

                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={handleDownloadPDF}
                                className="flex-1 bg-[#FFFDF7] text-[#15302B] border-2 border-[rgba(21,48,43,0.10)] px-8 py-4 rounded-2xl font-bold flex items-center justify-center gap-3 hover:bg-[#f9f5ea] transition-all"
                            >
                                <Download className="w-5 h-5" />
                                Download PDF
                            </motion.button>
                        </div>
                    </div>

                    {/* Footer Info */}
                    <div className="bg-slate-50 p-6 border-t border-slate-100 text-center">
                        <div className="flex items-center justify-center gap-2 text-slate-500 text-sm">
                            <ShieldCheck className="w-4 h-4 text-emerald-500" />
                            Secure transaction processed by Metafit Payment Gateway
                        </div>
                    </div>
                </motion.div>

                {/* Help Section */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8 }}
                    className="mt-10 p-8 rounded-3xl bg-white border border-slate-100 shadow-sm text-center"
                >
                    <h3 className="text-slate-900 font-bold mb-4">Having issues? Contact our 24/7 support team</h3>
                    <div className="flex flex-col md:flex-row items-center justify-center gap-6">
                        <a href="tel:+918829912389" className="flex items-center gap-3 text-slate-600 hover:text-emerald-600 transition-colors group">
                            <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center group-hover:bg-emerald-50 transition-colors">
                                <Phone className="w-5 h-5" />
                            </div>
                            <span className="font-semibold">+91 88299 12389</span>
                        </a>
                        <a href="mailto:info@metafitwellness.com" className="flex items-center gap-3 text-slate-600 hover:text-emerald-600 transition-colors group">
                            <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center group-hover:bg-emerald-50 transition-colors">
                                <Mail className="w-5 h-5" />
                            </div>
                            <span className="font-semibold">info@metafitwellness.com</span>
                        </a>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default PaymentSuccess;
